import type { ComponentProps } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import {
  Content,
  Header,
  Page,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { errorApiRef, useApi, useRouteRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import type {
  FieldExtensionComponentProps,
  FieldExtensionOptions,
  ScaffolderRJSFFormProps,
} from '@backstage/plugin-scaffolder-react';
import {
  SecretsContextProvider,
  makeFieldSchema,
  scaffolderApiRef,
  useTemplateSecrets,
} from '@backstage/plugin-scaffolder-react';
import type { ReviewStateProps } from '@backstage/plugin-scaffolder-react/alpha';
import {
  Form,
  ReviewState,
  Stepper,
  formFieldsApiRef,
  useFilteredSchemaProperties,
  useTemplateParameterSchema,
} from '@backstage/plugin-scaffolder-react/alpha';
import scaffolderPlugin from '@backstage/plugin-scaffolder/alpha';
import { Card, makeStyles } from '@material-ui/core';
import { customizeValidator } from '@rjsf/validator-ajv8';
import { operationLabel } from '../platform/templates';
import { scaffolderErrorForDisplay } from './failure';
import { activeReview } from './reviewState';

// Skill: creating-backstage-scaffolder-field

const FirewallRulesForm = Form as (
  props: ScaffolderRJSFFormProps<Record<string, unknown>[]>,
) => JSX.Element;
const firewallRulesValidator = customizeValidator<Record<string, unknown>[]>();

const FirewallRulesField = ({
  schema,
  uiSchema: fieldUiSchema,
  idSchema,
  formData,
  onChange,
  disabled,
  readonly,
}: FieldExtensionComponentProps<Record<string, unknown>[]>) => {
  const uiSchema = { ...fieldUiSchema };
  delete uiSchema['ui:field'];
  return (
    <FirewallRulesForm
      schema={schema}
      uiSchema={uiSchema}
      idPrefix={idSchema.$id}
      validator={firewallRulesValidator}
      formData={formData}
      onChange={event => onChange(event.formData)}
      disabled={disabled}
      readonly={readonly}
      omitExtraData
      liveOmit
      tagName="div"
    >
      {null}
    </FirewallRulesForm>
  );
};

// The create templates whose `rules` list carries protocol-dependent fields:
// the firewall rule set (#14357) and the VPC tier ACL (#15784) share the rule
// shape, so both need the inner form that prunes the fields a changed protocol
// hides before the Stepper submits them.
const RULES_WIZARD_TEMPLATES = [
  'firewall-ruleset-create',
  'cloudstack-tier-acl-create',
];

export const firewallWizardProps = {
  extensions: [
    {
      name: 'FirewallRules',
      component: FirewallRulesField,
      schema: makeFieldSchema({
        output: zImpl => zImpl.array(zImpl.record(zImpl.unknown())),
      }).schema,
    },
  ],
  formProps: { uiSchema: { rules: { 'ui:field': 'FirewallRules' } } },
};

const useStyles = makeStyles(theme => ({
  // The prototype constrains the form to a single readable column rather than
  // letting inputs stretch to the width of the window: every field on every IDP
  // template is a short string, an enum or a size, and none of them read better
  // at 1300px.
  shell: {
    maxWidth: 700,
  },
  // The stock stepper is `alternativeLabel`, which centres each label under its
  // circle and shares the card width equally between the steps. The prototype
  // reads as one left-to-right line - circle, label, gap - so the labels are
  // pulled back beside the circles, the connector rules are dropped and the
  // steps stop stretching. Only `Mui*` class names are targeted: those survive
  // a production build, the `Backstage*` ones are minified to `jss<n>`.
  wizard: {
    '& .MuiStepper-root': {
      background: 'transparent',
      justifyContent: 'flex-start',
      padding: theme.spacing(2, 2.5, 1),
    },
    '& .MuiStep-root': {
      flex: '0 0 auto',
      paddingRight: theme.spacing(3.25),
    },
    '& .MuiStepConnector-root': {
      display: 'none',
    },
    '& .MuiStepLabel-root': {
      flexDirection: 'row',
    },
    '& .MuiStepLabel-labelContainer': {
      width: 'auto',
      textAlign: 'left',
    },
    '& .MuiStepLabel-label.MuiStepLabel-alternativeLabel': {
      fontSize: 13,
      fontWeight: 600,
      marginLeft: theme.spacing(1.25),
      marginTop: 0,
    },
  },
  // The review step is the platform's last chance to show what is about to be
  // ordered, so the label column is fixed and set in the same small-caps the
  // rest of the prototype uses for key/value pairs - a value never lines up
  // against a label that has wrapped away from it.
  review: {
    '& .MuiTableRow-root > .MuiTableCell-root:first-child': {
      color: theme.palette.text.secondary,
      fontSize: 11,
      letterSpacing: '1.1px',
      textTransform: 'uppercase',
      width: 200,
    },
  },
}));

/**
 * The wizard's own review step, wrapped rather than replaced.
 *
 * Deriving the summary from the form state is not a rendering concern: the
 * stock component is what applies `ui:backstage.review` masks and blanks out
 * password and secret fields, so a hand-written key/value list here would put
 * credentials on screen. Ours are the styling and `activeReview`, which drops
 * values of form branches the user left.
 */
const ReviewSummary = (props: ReviewStateProps) => {
  const classes = useStyles();
  return (
    <div className={classes.review}>
      <ReviewState {...activeReview(props)} />
    </div>
  );
};

/**
 * The custom fields every IDP template is written against - TenantPicker,
 * ApplicationGroupPicker, DestinationPicker and the rest - plus the pickers the
 * scaffolder ships. Both arrive through the same API, so a template that names
 * a field this app never registered fails the same way it does upstream.
 *
 * `loadFormFields` hands back the opaque `FormField` wrapper the scaffolder
 * uses to version its extension contract. The wrapper IS the object that
 * `createFormField` was given (`Object.assign(props, { $$type, version })`), but
 * the unwrapper is internal to the plugin, so the shape is asserted here.
 */
const useFormFields = (): FieldExtensionOptions[] => {
  const formFieldsApi = useApi(formFieldsApiRef);
  const [fields, setFields] = useState<FieldExtensionOptions[]>([]);

  useEffect(() => {
    let active = true;
    formFieldsApi
      .loadFormFields()
      .then(loaded => {
        if (active) setFields(loaded as unknown as FieldExtensionOptions[]);
      })
      .catch(() => {
        // A field that fails to load is reported by the wizard as an unknown
        // `ui:field`, which names the offending field; swallowing the loader
        // error here keeps the form reachable for the steps that do resolve.
        if (active) setFields([]);
      });
    return () => {
      active = false;
    };
  }, [formFieldsApi]);

  return fields;
};

/**
 * The Template entity behind the form.
 *
 * The parameter schema the wizard runs on carries the title, the description
 * and the steps, but not what the template actually does - that lives on the
 * catalog entity as `spec.operation`. A failed read costs the labels only, and
 * they fall back to the creation wording, so the form stays usable.
 */
const useTemplateEntity = (templateRef: string): Entity | undefined => {
  const catalogApi = useApi(catalogApiRef);
  const [entity, setEntity] = useState<Entity | undefined>();

  useEffect(() => {
    let active = true;
    catalogApi
      .getEntityByRef(templateRef)
      .then(found => {
        if (active) setEntity(found);
      })
      .catch(() => {
        if (active) setEntity(undefined);
      });
    return () => {
      active = false;
    };
  }, [catalogApi, templateRef]);

  return entity;
};

/**
 * The scaffolder form, in the prototype's layout.
 *
 * The page is assembled from the scaffolder's own `Stepper` rather than its
 * `Workflow`, because `Workflow` puts the template title and description in a
 * card header - and in this layout they belong in the page header, above the
 * breadcrumb that says where the user came from. Everything below the header
 * is stock: the same steps, the same validation, the same review step.
 */
const TemplateForm = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const scaffolderApi = useApi(scaffolderApiRef);
  const errorApi = useApi(errorApiRef);
  const { secrets } = useTemplateSecrets();
  const extensions = useFormFields();

  const createRoute = useRouteRef(scaffolderPlugin.routes.root);
  const taskRoute = useRouteRef(scaffolderPlugin.routes.ongoingTask);

  // The route is `/create/templates/:namespace/:templateName`, so both params
  // are always present by the time this renders.
  const { namespace = 'default', templateName = '' } = useParams();
  const firewallForm = RULES_WIZARD_TEMPLATES.includes(templateName)
    ? firewallWizardProps
    : undefined;
  const templateRef = stringifyEntityRef({
    kind: 'Template',
    namespace,
    name: templateName,
  });

  const { loading, manifest, error } = useTemplateParameterSchema(templateRef);
  // One wizard serves every operation, so the wording has to come from the
  // template rather than from the page: the destroy form used to confirm with
  // a "Create" button, under a title saying the Stack was about to be torn
  // down (#14005). A template that performs anything other than a creation was
  // also not reached through the template picker, so the breadcrumb leads back
  // to the Stack list instead of claiming the user is creating something.
  const templateEntity = useTemplateEntity(templateRef);
  const submitLabel = templateEntity
    ? operationLabel(templateEntity)
    : 'Create';
  const isDayTwo = submitLabel !== 'Create';
  // Drops the steps and properties behind a feature flag the user does not
  // have, which is what the stepper is then counting when it numbers them.
  const shownManifest = useFilteredSchemaProperties(manifest);

  const onCreate = useCallback<ComponentProps<typeof Stepper>['onCreate']>(
    async values => {
      try {
        const { taskId } = await scaffolderApi.scaffold({
          templateRef,
          values,
          secrets,
        });
        navigate(taskRoute({ taskId }));
      } catch (err) {
        // `Stepper.handleCreate` wraps this in try/finally with no catch, so
        // without this the submit button would simply re-enable and say
        // nothing when the order is rejected - a 403 for a tenant not
        // entitled to the template, a quota refusal, or the orchestrator
        // being down. The stock wizard routes the same failure to errorApi.
        //
        // What reaches errorApi is classified first: the raw value is the
        // status line with the response body stringified onto the end of it,
        // which tells the reader neither what was refused nor whether it is
        // theirs to fix.
        errorApi.post(scaffolderErrorForDisplay(err));
      }
    },
    [errorApi, navigate, scaffolderApi, secrets, taskRoute, templateRef],
  );

  return (
    <Page themeId="home">
      <Header
        // `type` plus `typeLink` is what renders the breadcrumb: the first
        // crumb is the way back to where the form was reached from, which is
        // the only navigation this page has now that it owns its own chrome.
        type={isDayTwo ? 'My stacks' : 'Create'}
        typeLink={isDayTwo ? '/my-stacks' : createRoute()}
        title={manifest?.title ?? templateName}
        subtitle={manifest?.description}
      />
      <Content>
        {loading && <Progress />}

        {!loading && error && (
          <WarningPanel title="Could not load this template" severity="error">
            {error.message}
          </WarningPanel>
        )}

        {!loading && !error && shownManifest && (
          <Card className={`${classes.shell} ${classes.wizard}`}>
            <Stepper
              manifest={shownManifest}
              extensions={[...extensions, ...(firewallForm?.extensions ?? [])]}
              formProps={firewallForm?.formProps}
              onCreate={onCreate}
              components={{
                ReviewStateComponent: ReviewSummary,
                createButtonText: submitLabel,
              }}
            />
          </Card>
        )}
      </Content>
    </Page>
  );
};

/**
 * Secrets are collected by the form and posted with the scaffold call, but they
 * are deliberately kept out of the form state so they never reach the review
 * step or a browser history entry - which is what the provider is for.
 */
export const TemplateFormPage = () => (
  <SecretsContextProvider>
    <TemplateForm />
  </SecretsContextProvider>
);
